import Layout from "../Layout.tsx";
import { Navigate, useSearchParams } from "@solidjs/router";
import type { JSX } from "solid-js";
import { For, Match, Switch } from "solid-js";
import UserForm from "./UserForm.tsx";
import { Dynamic } from "solid-js/web";
import { isNil } from "lodash-es";
import CompanyForm from "./CompanyForm.tsx";
import PersonaliseForm from "./PersonaliseForm.tsx";
import type { NavigationProps } from "./OnboardingNavigation.tsx";
import { OnboardingNavigation } from "./OnboardingNavigation.tsx";
import TemplatePage from "./TemplatePage.tsx";
import { useUserState } from "../../state/user.tsx";
import { useCustomerState } from "../../state/customer.tsx";

export type StepFormProps = {
  next: () => void;
  back: () => void;
  navigation: (props: NavigationProps) => JSX.Element;
};

type OnboardingStep = {
  key: string;
  component: (props: StepFormProps) => JSX.Element;
};

const OnboardingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams<{ step: string }>();
  const user = useUserState();
  const customer = useCustomerState();

  const steps: OnboardingStep[] = [
    { key: "user", component: UserForm },
    { key: "company", component: CompanyForm },
    { key: "personalise", component: PersonaliseForm },
  ];

  return (
    <Layout>
      <Switch fallback={<Navigate href="?step=user" />}>
        <Match when={user().finishedTour}>
          <Navigate href={`/customers/${customer.customerId}`} />
        </Match>
        <For each={steps}>
          {(step, i) => (
            <Match when={searchParams.step === step.key}>
              <Dynamic
                component={step.component}
                navigation={OnboardingNavigation({ current: i() + 1, total: steps.length })}
                next={() => {
                  const nextStep = steps[i() + 1];
                  const step = isNil(nextStep) ? "template" : nextStep.key;
                  setSearchParams({ step }, { replace: true });
                }}
                back={() => {
                  const previousStep = steps[i() - 1];
                  if (isNil(previousStep)) {
                    return;
                  }
                  setSearchParams({ step: previousStep.key }, { replace: true });
                }}
              />
            </Match>
          )}
        </For>
        <Match when={searchParams.step === "template"}>
          <TemplatePage />
        </Match>
      </Switch>
    </Layout>
  );
};

export default OnboardingPage;
